import React from 'react'
import {
	ResponsiveContainer,
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	Bar,
	Line,
} from 'recharts'
import type { ProcessedMonthRow } from '../../../utils/overdueInvoiceAging'

export interface MonthlyTrendsChartProps {
	rows: ProcessedMonthRow[]
	/** e.g. "2025–2026" — shown next to title when set */
	yearRangeLabel?: string
}

export const MonthlyTrendsChart: React.FC<MonthlyTrendsChartProps> = ({
	rows,
	yearRangeLabel,
}) => {
	const data = rows.map(month => ({
		month:
			String(month.month_label || '') ||
			(month.period_start_date
				? new Date(String(month.period_start_date)).toLocaleDateString('en-US', {
						month: 'short',
						year: 'numeric',
					})
				: ''),
		overdueInvoices: Number(month.overdue_invoices || month.month_overdue_invoices || 0),
		paymentRate: Number(month.month_payment_rate || 0),
	}))

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
			<div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
				<h3 className="text-[12px] font-bold text-slate-800">Monthly overdue trends</h3>
				{yearRangeLabel ? (
					<span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
						{yearRangeLabel}
					</span>
				) : (
					<span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
						Current &amp; last year
					</span>
				)}
			</div>
			<div className="h-[200px] w-full min-w-0">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
						<XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} stroke="#94a3b8" />
						<YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} stroke="#94a3b8" />
						<YAxis
							yAxisId="right"
							orientation="right"
							tick={{ fontSize: 10, fill: '#64748b' }}
							stroke="#94a3b8"
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: '#fff',
								border: '1px solid #e2e8f0',
								borderRadius: 8,
								fontSize: 12,
							}}
						/>
						<Legend wrapperStyle={{ fontSize: 11 }} />
						<Bar
							yAxisId="left"
							dataKey="overdueInvoices"
							fill="#fb7185"
							name="Overdue invoices"
							radius={[2, 2, 0, 0]}
						/>
						<Line
							yAxisId="right"
							type="monotone"
							dataKey="paymentRate"
							stroke="#10b981"
							strokeWidth={2}
							name="Payment rate %"
							dot={{ fill: '#10b981', r: 3 }}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}
